import { LazyStore } from "../../utils/Store";
import type { ArticleData } from "../../model/ArticleTypes";
import { createDummyArticle, extractArticleData } from "../../model/ArticleTypes";
import type { CommonCartItem } from "../../model/CommonCartTypes";
import type { SessionContext } from "../../model/CustomerCartTypes";
import { CustomerCartContextStore } from "./CartContextStore";
import { getArticleData } from "../../server/ArticleServerRequests";
import type { AnonCartContext, AnonCartItem } from "../../model/AnonCartTypes";
import { getAnonCartArticleData } from "../../server/AnonCartArticlesRequests";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
export type ArticleDataWatchListEntry = {
	timestamp: number;
	data: ArticleData;
	owner: Set<string>;
};
const REFRESH_TIME_THRESHOLD_MS = 10 * 60 * 1000;
const REFRESH_INTERVAL_MAX_MS = 15000;
const REFRESH_INTERVAL_MIN_MS = 1000;
export type ArticleDataProvider<ContextType> = (arg0: ContextType, arg1: string[]) => Promise<Map<string, ArticleData>>;
export type ContextProvider<ContextType> = () => ContextType | null | undefined;
export function updateEntry<K, V>(map: Map<K, V>, key: K, update: (arg0: V) => V) {
	if (!map.has(key)) return;
	const oldValue = map.get(key);
	if (oldValue === null || typeof oldValue === "undefined") return;
	map.set(key, update(oldValue));
}
export class CartItemStoreImpl<ContextType> extends LazyStore {
	_watchList: Map<string, ArticleDataWatchListEntry> = new Map();
	_currentUpdateJob: any;
	_fetchArticleData: ArticleDataProvider<ContextType>;
	_getContext: ContextProvider<ContextType>;

	constructor(getContext: ContextProvider<ContextType>, fetchArticleData: ArticleDataProvider<ContextType>) {
		super();
		this._fetchArticleData = fetchArticleData;
		this._getContext = getContext;

		this.onArm = () => {
			this.executeScheduledUpdate().catch((error) => {
				console.error("executeScheduledUpdate in CartItemStore failed with ", error);
			});
		};

		this.onDisarm = () => {
			if (this._currentUpdateJob) {
				clearTimeout(this._currentUpdateJob);
				this._currentUpdateJob = null;
			}
		};
	}

	_getUpdateDelay(): number {
		let oldestAge = 0;
		let needsImmediateUpdate = false;

		for (const article of this.articleData.values()) {
			if (this._isEmptyArticleData(article)) {
				needsImmediateUpdate = true;
				break;
			}

			if (typeof article.dataAgeMs !== "undefined" && article.dataAgeMs > oldestAge) {
				oldestAge = article.dataAgeMs;
			}
		}

		const timeToOutdated = REFRESH_TIME_THRESHOLD_MS - oldestAge;
		return needsImmediateUpdate
			? REFRESH_INTERVAL_MIN_MS
			: Math.max(REFRESH_INTERVAL_MIN_MS, Math.min(timeToOutdated, REFRESH_INTERVAL_MAX_MS));
	}

	async executeScheduledUpdate(tryCount = 1) {
		if (this._currentUpdateJob) {
			clearTimeout(this._currentUpdateJob);
			this._currentUpdateJob = null;
		}

		const successful = await this.updateData();

		const baseDelay = this._getUpdateDelay();

		const delay = successful ? baseDelay : baseDelay * tryCount;
		this._currentUpdateJob = setTimeout(() => this.executeScheduledUpdate(successful ? 1 : tryCount + 1), delay);
	}

	refreshWatchEntries(items: Record<string, AnonCartItem | CommonCartItem | null | undefined>, owner: string) {
		// add new entries to watchlist
		const ownerItemSet = new Set();
		let needsImmediateUpdate = false;

		for (const bundleId in items) {
			if (!Object.hasOwn(items, bundleId)) continue;
			ownerItemSet.add(bundleId);
			const item = items[bundleId];

			if (!this._watchList.has(bundleId)) {
				this._watchList.set(bundleId, {
					timestamp: Date.now(),
					data: extractArticleData(item) || createDummyArticle(bundleId),
					owner: new Set([owner]),
				});

				// any new item data has to be fetched immediately.
				needsImmediateUpdate = true;
			} else {
				updateEntry(this._watchList, bundleId, (entry) => ({ ...entry, owner: entry.owner.add(owner) }));
			}
		}

		// remove owner from items that are not owned by this owner
		[...this._watchList.keys()]
			.filter((bundleId) => !ownerItemSet.has(bundleId))
			.forEach((bundleId) => {
				updateEntry(this._watchList, bundleId, (entry) => ({
					...entry,
					owner: new Set([...entry.owner.values()].filter((it) => it !== owner)),
				}));

				// delete watchlist entries, which have no owners left
				const entry = this._watchList.get(bundleId);

				if (entry && entry.owner.size === 0) {
					this._watchList.delete(bundleId);
				}
			});

		if (needsImmediateUpdate) {
			this.executeScheduledUpdate().catch(() => {});
		}
	}

	async updateData(): Promise<boolean> {
		const cartContext = this._getContext();

		if (!cartContext) return false;
		// get all bundle ids, which we have to request
		const bundleIdsToRequest = [...this._watchList.entries()]
			.filter(([, entry]) => CartItemStoreImpl.isOutdated(entry))
			.map((entry) => entry[0]);

		if (bundleIdsToRequest.length === 0) {
			return true;
		}

		try {
			// request them remotely
			const remoteData = await this._fetchArticleDataBatched(cartContext, bundleIdsToRequest);

			// and put them back into our watchlist.
			for (const [key, data] of remoteData.entries()) {
				updateEntry(this._watchList, key, (entry) => ({ ...entry, timestamp: Date.now(), data }));
			}
		} catch (e) {
			return false;
		}

		this.trigger();
		return true;
	}

	async _fetchArticleDataBatched(cartContext: ContextType, bundleIds: string[]): Promise<Map<string, ArticleData>> {
		// We have to batch requests, because if we have too many items, the request header size limits
		// may be exceeded.
		const BATCH_SIZE = 10;
		const requests = [] as any[];
		let batch: Set<string> = new Set();

		for (const bundleId of bundleIds) {
			batch.add(bundleId);

			if (batch.size === BATCH_SIZE) {
				requests.push(this._fetchArticleData(cartContext, [...batch]));
				batch = new Set();
			}
		}

		// don't miss the last batch!
		if (batch.size > 0) {
			requests.push(this._fetchArticleData(cartContext, [...batch]));
		}

		const result: Map<string, ArticleData> = new Map();
		const responses = await Promise.all(requests);

		for (const response of responses) {
			for (const [key, value] of response) {
				result.set(key, value);
			}
		}

		return result;
	}

	_isEmptyArticleData(data: ArticleData): boolean {
		return !!data.missingReasons && data.missingReasons.includes("PENDING");
	}

	get watchList(): Map<string, ArticleDataWatchListEntry> {
		return this._watchList;
	}

	get articleData(): Map<string, ArticleData> {
		return new Map(
			[...this._watchList.entries()].map((entry) => [
				entry[0],
				{ ...entry[1].data, outdated: CartItemStoreImpl.isOutdated(entry[1]) },
			]),
		);
	}

	static isOutdated(entry: ArticleDataWatchListEntry): boolean {
		return (
			(!entry.data.dataAgeMs ? 0 : entry.data.dataAgeMs) + Date.now() - entry.timestamp >
				REFRESH_TIME_THRESHOLD_MS || (entry.data.missingReasons || []).indexOf("PENDING") >= 0
		);
	}
}
export const AnonCartItemStore: CartItemStoreImpl<AnonCartContext> = new CartItemStoreImpl(() => {
	const country = SessionStore.getCountry();
	const storeId = SessionStore.getSelectedStoreId() || SessionStore.getDefaultStoreForAnon();
	const anonUserId = SessionStore.getAnonymousUserId();
	const locale = SessionStore.getLocale();

	if (!country || !storeId || !anonUserId || !locale) {
		return null;
	}

	return {
		country,
		locale,
		storeId,
		anonUserId,
	};
}, getAnonCartArticleData);
export const CustomerCartItemStore: CartItemStoreImpl<SessionContext> = new CartItemStoreImpl(() => {
	const { customerId, cardholderNumber, storeId, fsdAddressId, country, locale } =
		CustomerCartContextStore.getState();

	if (!customerId || !cardholderNumber || !storeId || !country || !locale) {
		return null;
	}

	return {
		customerId,
		cardholderNumber,
		storeId,
		fsdAddressId,
		country,
		locale,
	};
}, getArticleData);
